import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useToolRuns } from "../../hooks";

const STATUS_LABEL: Record<string, string> = {
  ok: "OK",
  failed: "Failed",
  timeout: "Timed out",
  ssrf_blocked: "Blocked",
};

/** Recent executions of one API Request tool — the answer to "did my webhook
 * actually fire, and what did it say back". */
export default function ToolRuns({ toolId }: { toolId: string }) {
  const q = useToolRuns(toolId, true);

  if (q.isLoading) {
    return (
      <div className="mt-3 space-y-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  const runs = q.data ?? [];
  if (!runs.length) {
    return (
      <p className="mt-3 text-xs text-muted-foreground">
        This tool hasn't run yet. Try it from Talk.
      </p>
    );
  }

  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-muted-foreground">
          <tr className="text-left">
            <th className="py-1 pr-3 font-medium">When</th>
            <th className="py-1 pr-3 font-medium">Result</th>
            <th className="py-1 pr-3 font-medium">Took</th>
            <th className="py-1 font-medium">Detail</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => (
            <tr key={run.id} className="border-t">
              <td className="whitespace-nowrap py-1.5 pr-3 text-muted-foreground">
                {new Date(run.created_at).toLocaleString()}
              </td>
              <td className="py-1.5 pr-3">
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[11px]",
                    run.status === "ok"
                      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                      : "bg-destructive/10 text-destructive",
                  )}
                >
                  {STATUS_LABEL[run.status] ?? run.status}
                  {run.response_code ? ` ${run.response_code}` : ""}
                </span>
              </td>
              <td className="whitespace-nowrap py-1.5 pr-3 text-muted-foreground">
                {run.duration_ms} ms
              </td>
              <td className="max-w-[24rem] truncate py-1.5 font-mono text-[11px] text-muted-foreground">
                {run.error_detail || run.response_body}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
